import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatValue } from "@/lib/api";
import {
  Loader2, Gauge, Crosshair, TrendingUp, TrendingDown, AlertTriangle, ArrowUpRight,
  ChevronRight, ExternalLink, Sparkles, Target, ListChecks,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DmeTag from "@/components/DmeTag";
import PlayerTag from "@/components/PlayerTag";

const WINDOW_STYLE = {
  Contend: { c: "var(--accent)", b: "var(--accent)" },
  Reload: { c: "var(--accent)", b: "var(--accent)" },
  Rebuild: { c: "#60A5FA", b: "#60A5FA" },
  Retool: { c: "var(--amber)", b: "var(--amber)" },
  "Sell the Vets": { c: "var(--amber)", b: "var(--amber)" },
  "On the Bubble": { c: "var(--text-2)", b: "var(--border)" },
};

const ACTION_ICON = {
  buy_low: Crosshair, sell_high: ArrowUpRight, verdict: Gauge, roster_risk: AlertTriangle,
};

const SectionLabel = ({ icon: Icon, children, color }) => (
  <div className="flex items-center gap-2 text-xs tracking-widest text-[var(--text-3)] mb-3 font-mono">
    {Icon && <Icon className="w-3.5 h-3.5" style={{ color: color || "var(--accent)" }} />} {children}
  </div>
);

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/command-center").then(r => setData(r.data)).catch(() => setErr(true));
  }, []);

  if (err) return <div className="p-8 text-sm text-[var(--text-3)]" data-testid="cc-error">Couldn't load your Command Center. Try again shortly.</div>;
  if (!data) return (
    <div className="flex flex-col items-center justify-center gap-3 py-24" data-testid="cc-loading">
      <Loader2 className="w-6 h-6 spin text-[var(--accent)]" />
      <div className="text-xs text-[var(--text-3)]">Reading every roster, pick & trade signal across your leagues…</div>
    </div>
  );

  const noLeagues = (data.leagues_scanned || 0) === 0;

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="command-center">
      <div>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] text-[var(--accent)] font-mono mb-2">
          <Gauge className="w-3.5 h-3.5" /> COMMAND CENTER
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Your top moves, right now</h1>
        <p className="text-base text-[var(--text-2)] mt-2 max-w-2xl">
          Every roster, pick and trade signal across your leagues — distilled into the highest-leverage actions to win.
        </p>
      </div>

      {noLeagues ? (
        <div className="card-bordered p-8 text-center" data-testid="cc-empty">
          <p className="text-sm text-[var(--text-2)]">Sync a Sleeper league to unlock your Command Center.</p>
          <Link to="/app" className="btn-primary inline-block mt-4 text-sm">Sync a league</Link>
        </div>
      ) : (
        <>
          {/* AI STRATEGIST BRIEFING */}
          {data.ai_briefing && (
            <section className="card-bordered p-5 border-[var(--accent)]/25 bg-[var(--accent)]/[0.03]" data-testid="cc-ai-briefing">
              <SectionLabel icon={Sparkles}>AI STRATEGIST BRIEFING</SectionLabel>
              <div className="prose-dme text-sm text-[var(--text-2)] max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.ai_briefing}</ReactMarkdown>
              </div>
            </section>
          )}

          {/* ACTION FEED */}
          <section>
            <SectionLabel>PRIORITY ACTIONS</SectionLabel>
            {data.actions.length === 0 ? (
              <div className="card-bordered p-6 text-sm text-[var(--text-3)]">No urgent moves right now — your leagues are steady. Check back as the market shifts.</div>
            ) : (
              <div className="space-y-2">
                {data.actions.map((a, i) => {
                  const Icon = ACTION_ICON[a.type] || Gauge;
                  return (
                    <div key={i} className="card-bordered p-4 flex items-center gap-4 hover:border-[var(--accent)]/50 transition-colors group" data-testid={`cc-action-${i}`}>
                      <Link to={a.cta_to} className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-sm bg-black/30 border border-[var(--border)] flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-[var(--accent)]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{a.title}</div>
                          <div className="text-xs text-[var(--text-2)] truncate">{a.detail}</div>
                        </div>
                      </Link>
                      <div className="hidden sm:flex items-center gap-3 shrink-0">
                        {a.league_id && (
                          <a href={`https://sleeper.com/leagues/${a.league_id}`} target="_blank" rel="noreferrer"
                            className="text-xs text-[var(--text-3)] hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid={`cc-sleeper-${i}`}
                            title="Open this league on Sleeper to make the move">
                            Sleeper <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        <Link to={a.cta_to} className="flex items-center gap-1 text-xs text-[var(--accent)] opacity-70 group-hover:opacity-100">
                          {a.cta_label} <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* BUY-LOW TARGETS */}
          {(data.buy_low || []).length > 0 && (
            <section>
              <SectionLabel icon={Target}>BUY-LOW TARGETS · ACROSS YOUR LEAGUES <DmeTag /></SectionLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="cc-buylow">
                {data.buy_low.map((b, i) => (
                  <div key={`${b.league_id}-${b.player_id}`} className="card-bordered p-4" data-testid={`cc-buy-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <PlayerTag player={b} size={28} link={b.kind !== "pick"} showPos />
                        <div className="text-[10px] tracking-widest text-[var(--text-3)] mt-0.5 ml-9">from {b.owner_name}</div>
                      </div>
                      <div className="font-mono text-[var(--accent)] shrink-0">{formatValue(b.value)}</div>
                    </div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] mt-1">{b.league_name} · acquirability {b.acquirability}</div>
                    {(b.signals || []).length > 0 && (
                      <div className="text-xs text-[var(--text-2)] mt-1.5">{b.signals[0]}</div>
                    )}
                    <Link to="/app/portfolio?tab=tradedesk" className="inline-flex items-center gap-1 text-xs text-[var(--accent)] mt-2 opacity-80 hover:opacity-100">
                      Open Trade Desk <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* VERDICTS + BLUEPRINT */}
          <section>
            <SectionLabel>CONTEND OR REBUILD · BY LEAGUE</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.verdicts.map((v, i) => {
                const st = WINDOW_STYLE[v.window] || WINDOW_STYLE["On the Bubble"];
                return (
                  <div key={i} className="card-bordered p-5" data-testid={`cc-verdict-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-display font-bold truncate">{v.league_name}</div>
                      <span className="text-[10px] tracking-widest font-mono px-2 py-1 rounded-sm" style={{ color: st.c, border: `1px solid ${st.b}` }} data-testid={`cc-window-${i}`}>{v.window.toUpperCase()}</span>
                    </div>
                    <p className="text-sm text-[var(--text-2)] mb-3">{v.headline}</p>
                    <div className="grid grid-cols-3 gap-2 text-center mb-3">
                      <div><div className="font-mono text-lg">{v.my_rank}<span className="text-[var(--text-3)] text-sm">/{v.total_teams}</span></div><div className="text-[9px] tracking-widest text-[var(--text-3)]">VALUE RANK</div></div>
                      <div><div className="font-mono text-lg" style={{ color: st.c }}>{v.window_score}</div><div className="text-[9px] tracking-widest text-[var(--text-3)]">WIN-NOW</div></div>
                      <div><div className="font-mono text-lg">{v.core_age ?? "—"}</div><div className="text-[9px] tracking-widest text-[var(--text-3)]">CORE AGE</div></div>
                    </div>
                    {(v.needs || []).length > 0 && (
                      <div className="flex items-center flex-wrap gap-1.5 mb-3" data-testid={`cc-needs-${i}`}>
                        <span className="text-[9px] tracking-widest text-[var(--text-3)] mr-1">THIN AT</span>
                        {v.needs.map(n => (
                          <span key={n} className="text-[10px] font-mono font-bold rounded px-1.5 py-0.5 leading-none" style={{ color: "var(--amber)", border: "1px solid var(--amber)55", background: "var(--amber)14" }}>{n}</span>
                        ))}
                      </div>
                    )}
                    {(v.blueprint || []).length > 0 && (
                      <div className="border-t border-[var(--border)] pt-3" data-testid={`cc-blueprint-${i}`}>
                        <div className="flex items-center gap-1.5 text-[9px] tracking-widest text-[var(--text-3)] mb-2"><ListChecks className="w-3 h-3" /> YOUR PLAN</div>
                        <ul className="space-y-1.5">
                          {v.blueprint.map((step, k) => (
                            <li key={k} className="text-xs text-[var(--text-2)] flex gap-2">
                              <span className="text-[var(--accent)] font-mono shrink-0">{k + 1}.</span><span>{step}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ROSTER VALUE ALERTS */}
          {(data.roster_alerts || []).length > 0 && (
            <section>
              <SectionLabel icon={TrendingUp} color="var(--amber)">ROSTER VALUE ALERTS · YOUR PLAYERS <DmeTag /></SectionLabel>
              <div className="space-y-2" data-testid="cc-roster-alerts">
                {data.roster_alerts.map((s, i) => {
                  const down = s.direction === "down";
                  const Icon = down ? TrendingDown : TrendingUp;
                  const color = down ? "var(--amber)" : "var(--accent)";
                  return (
                    <div key={`${s.league_id}-${s.player_id}`} className="card-bordered p-4" data-testid={`cc-alert-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <PlayerTag player={s} size={28} showPos />
                          <div className="text-[10px] tracking-widest text-[var(--text-3)] mt-0.5 ml-9">{s.league_name}</div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-mono inline-flex items-center gap-1" style={{ color }}><Icon className="w-3.5 h-3.5" />{Math.abs(s.change_pct)}%</span>
                          <span className="font-mono text-[var(--text-2)]">{formatValue(s.value)}</span>
                        </div>
                      </div>
                      <div className="text-xs mt-1.5" style={{ color }}>{s.note}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* SELL-HIGH RADAR */}
          <section>
            <SectionLabel icon={TrendingUp} color="var(--amber)">SELL-HIGH RADAR <DmeTag /></SectionLabel>
            {data.sell_high.length === 0 ? (
              <div className="card-bordered p-6 text-sm text-[var(--text-3)]" data-testid="cc-sellhigh-empty">No sell-high flags right now — none of your assets are peaking or aging into a sell window.</div>
            ) : (
              <div className="space-y-2" data-testid="cc-sellhigh">
                {data.sell_high.map((s, i) => (
                  <div key={`${s.league_id}-${s.player_id}`} className="card-bordered p-4" data-testid={`cc-sell-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <PlayerTag player={s} size={28} showPos />
                        <div className="text-[10px] tracking-widest text-[var(--text-3)] mt-0.5 ml-9">{s.league_name}</div>
                      </div>
                      <div className="font-mono text-[var(--amber)] shrink-0">{formatValue(s.value)}</div>
                    </div>
                    <div className="text-xs text-[var(--text-2)] mt-1.5">{s.reasons.join(" · ")}</div>
                    <div className="text-xs text-[var(--accent)] mt-1">{s.suggestion}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
